<div class="dialog-title-row">
  <h2 mat-dialog-title>{{ isEdit ? 'Edit' : 'Create' }} Codebook</h2>
  <div class="mode-toggle">
    <button mat-button [class.active]="editMode === 'form'" (click)="switchMode('form')">
      <mat-icon>edit_note</mat-icon> Form
    </button>
    <button mat-button [class.active]="editMode === 'json'" (click)="switchMode('json')">
      <mat-icon>code</mat-icon> JSON
    </button>
  </div>
</div>

<mat-dialog-content class="dialog-content">
  <!-- ==================== FORM MODE ==================== -->
  <ng-container *ngIf="editMode === 'form'">
    <!-- Basic Information -->
    <div class="form-section">
      <h3 class="section-title">
        <mat-icon>info</mat-icon>
        Basic Information
      </h3>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Name</mat-label>
        <input matInput [(ngModel)]="detail.name" placeholder="e.g. My Codebook" />
      </mat-form-field>

      <div class="form-row">
        <mat-form-field appearance="outline" class="revision-field">
          <mat-label>Revision</mat-label>
          <input matInput type="number" [(ngModel)]="detail.revision" min="1" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="flex-grow">
          <mat-label>Description</mat-label>
          <input matInput [(ngModel)]="detail.description" placeholder="Brief description of this codebook" />
        </mat-form-field>
      </div>
    </div>

    <!-- Naming Levels -->
    <div class="form-section">
      <div class="section-header">
        <h3 class="section-title">
          <mat-icon>layers</mat-icon>
          Naming Levels
        </h3>
        <button mat-raised-button color="primary" (click)="addLevel()"><mat-icon>add</mat-icon> Add Level</button>
      </div>

      <p *ngIf="detail.namingLevels.length === 0" class="empty-message">
        No naming levels defined yet. Click <strong>"Add Level"</strong> to create one.
      </p>

      <mat-accordion multi class="levels-accordion">
        <mat-expansion-panel *ngFor="let level of detail.namingLevels; let i = index" class="level-panel">
          <mat-expansion-panel-header>
            <mat-panel-title class="level-title">
              <span class="level-badge">{{ level.levelNumber }}</span>
              {{ level.name || '(unnamed)' }}
            </mat-panel-title>
            <mat-panel-description>
              {{ level.description | slice: 0:50 }}{{ level.description?.length > 50 ? '...' : '' }}
            </mat-panel-description>
          </mat-expansion-panel-header>

          <div class="level-content">
            <div class="form-row">
              <mat-form-field appearance="outline" class="level-number-field">
                <mat-label>Level Number</mat-label>
                <input
                  matInput
                  type="number"
                  [(ngModel)]="level.levelNumber"
                  min="0"
                  (change)="onLevelNumberChange()"
                />
              </mat-form-field>

              <mat-form-field appearance="outline" class="flex-grow">
                <mat-label>Level Name</mat-label>
                <input matInput [(ngModel)]="level.name" placeholder="e.g. Equipment Type" />
              </mat-form-field>
            </div>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Description</mat-label>
              <textarea
                matInput
                [(ngModel)]="level.description"
                rows="2"
                placeholder="Describe what this level represents"
              ></textarea>
            </mat-form-field>

            <div class="toggle-row">
              <mat-slide-toggle [(ngModel)]="level.canBeOmitted" color="primary"> Can be omitted </mat-slide-toggle>
              <span class="toggle-hint">When enabled, this level is optional in the tag name</span>
            </div>

            <!-- Validation Rule -->
            <div class="validation-section">
              <h4 class="subsection-title">
                <mat-icon>rule</mat-icon>
                Validation Rule
              </h4>

              <div class="form-row">
                <mat-form-field appearance="outline">
                  <mat-label>Validation Type</mat-label>
                  <mat-select [(ngModel)]="level.validationRule.validationType">
                    <mat-option value="Regex">Regex</mat-option>
                    <mat-option value="AllowedValues">Allowed Values</mat-option>
                  </mat-select>
                </mat-form-field>

                <mat-form-field
                  appearance="outline"
                  class="flex-grow"
                  *ngIf="level.validationRule.validationType === 'Regex'"
                >
                  <mat-label>Regex Pattern</mat-label>
                  <input
                    matInput
                    [(ngModel)]="level.validationRule.regex"
                    placeholder="e.g. ^[A-Z]{2,4}$"
                    class="mono-input"
                  />
                </mat-form-field>
              </div>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Rule Description</mat-label>
                <input
                  matInput
                  [(ngModel)]="level.validationRule.description"
                  placeholder="Describe the validation rule"
                />
              </mat-form-field>

              <!-- Allowed Values -->
              <div *ngIf="level.validationRule.validationType === 'AllowedValues'" class="allowed-values-section">
                <div class="section-header">
                  <h5 class="subsection-title" style="margin: 0">
                    <mat-icon>list</mat-icon>
                    Allowed Values
                  </h5>
                  <button mat-mini-fab color="primary" (click)="addAllowedValue(level)" matTooltip="Add allowed value">
                    <mat-icon>add</mat-icon>
                  </button>
                </div>

                <p *ngIf="level.validationRule.allowedValues.length === 0" class="empty-message small">
                  No allowed values. Click <strong>+</strong> to add one.
                </p>

                <div class="allowed-values-list">
                  <div *ngFor="let av of level.validationRule.allowedValues; let j = index" class="allowed-value-row">
                    <mat-form-field appearance="outline" class="value-field">
                      <mat-label>Value</mat-label>
                      <input matInput [(ngModel)]="av.value" placeholder="e.g. PT" />
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="flex-grow">
                      <mat-label>Description</mat-label>
                      <input matInput [(ngModel)]="av.description" placeholder="e.g. Pressure Transmitter" />
                    </mat-form-field>

                    <button
                      mat-icon-button
                      color="warn"
                      (click)="removeAllowedValue(level, j)"
                      matTooltip="Remove this value"
                    >
                      <mat-icon>delete_outline</mat-icon>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <mat-action-row>
            <button mat-button color="warn" (click)="removeLevel(i)">
              <mat-icon>delete_outline</mat-icon> Remove Level
            </button>
          </mat-action-row>
        </mat-expansion-panel>
      </mat-accordion>
    </div>
  </ng-container>

  <!-- ==================== JSON MODE ==================== -->
  <ng-container *ngIf="editMode === 'json'">
    <div class="json-editor-section">
      <mat-form-field appearance="outline" class="full-width json-field">
        <mat-label>Codebook JSON</mat-label>
        <textarea matInput [(ngModel)]="jsonContent" rows="28" class="mono-input"></textarea>
      </mat-form-field>
      <div *ngIf="jsonError" class="json-error">{{ jsonError }}</div>
    </div>
  </ng-container>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button (click)="onCancelClick()">Cancel</button>
  <button
    mat-raised-button
    color="primary"
    [disabled]="editMode === 'form' ? !detail.name : !jsonContent"
    (click)="onSaveClick()"
  >
    <mat-icon>{{ isEdit ? 'save' : 'add' }}</mat-icon>
    {{ isEdit ? 'Update' : 'Create' }}
  </button>
</mat-dialog-actions>
